import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { nectarApi } from '../api/client'
import type { CefrLevel, NectarTask, PaginatedResponse, ProficiencyAssessmentFinalResult } from '../api/models'
import {
  completeAssessment,
  createAssessment,
  getAssessment,
  getAssessmentsByLanguage
} from '../api/proficiency-assessment'

export type AssessmentPhase =
  | 'loading'
  | 'setup'
  | 'claim-reward'
  | 'text-phase'
  | 'speech-phase'
  | 'completing'
  | 'results'

const MAX_POLL_RETRIES = 30
const POLL_INTERVAL_MS = 3000
const POLL_INITIAL_DELAY_MS = 2000

type UseProficiencyAssessmentOptions = {
  projectId: string
  language: string
  onBack: () => void
  onCompleted: () => void
}

export function useProficiencyAssessment({ projectId, language, onBack, onCompleted }: UseProficiencyAssessmentOptions) {
  const [phase, setPhase] = useState<AssessmentPhase>('loading')
  const [assessmentId, setAssessmentId] = useState<string | null>(null)
  const taskIdRef = useRef<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [finalResult, setFinalResult] = useState<ProficiencyAssessmentFinalResult | null>(null)
  const mountedRef = useRef(true)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const submitTask = useCallback(
    async (tid: string, aid: string) => {
      await nectarApi.post(`/v1/projects/${projectId}/tasks/${tid}/submissions`, { assessmentId: aid })
    },
    [projectId]
  )

  const pollForResult = useCallback(
    (id: string) => {
      let retries = 0
      const poll = async () => {
        if (!mountedRef.current) return
        retries++
        if (retries > MAX_POLL_RETRIES) {
          toast.error('Evaluation timed out. Check back later.')
          onBack()
          return
        }
        try {
          const data = await getAssessment(id)
          if (!mountedRef.current) return
          if (data.status === 'COMPLETED' && data.finalResult) {
            setFinalResult(data.finalResult)
            setPhase('results')
            if (taskIdRef.current) {
              try {
                await submitTask(taskIdRef.current, id)
                onCompleted()
              } catch {
                toast.error('Failed to submit task')
              }
            }
            return
          }
          if (data.status === 'FAILED') {
            toast.error('Assessment evaluation failed')
            onBack()
            return
          }
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS)
        } catch {
          toast.error('Failed to check status')
          onBack()
        }
      }
      pollTimeoutRef.current = setTimeout(poll, POLL_INITIAL_DELAY_MS)
    },
    [onBack, submitTask, onCompleted]
  )

  // Initialize: check existing assessments for this language
  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true

    const init = async () => {
      try {
        // 1. Fetch the single assigned task
        const taskData = await nectarApi.get<PaginatedResponse<NectarTask>>(`/v1/projects/${projectId}/tasks`, {
          page: 1,
          limit: 1
        })
        const task = taskData.data[0]
        if (!task) {
          toast.error('No task assigned')
          onBack()
          return
        }

        // Already submitted → done
        if (task.submissionCount > 0) {
          setPhase('results')
          return
        }

        taskIdRef.current = task.id

        // 2. Fetch all assessments for this language
        const assessments = await getAssessmentsByLanguage(language)
        const allAssessments = assessments.data

        // 3. Check for IN_PROGRESS → resume
        const inProgress = allAssessments.find((a) => a.status === 'IN_PROGRESS')
        if (inProgress) {
          setAssessmentId(inProgress.id)
          setExpiresAt(inProgress.expiresAt)

          const detail = await getAssessment(inProgress.id)

          if (detail.status === 'COMPLETED' && detail.finalResult) {
            // Finished while we were away — claim it
            setFinalResult(detail.finalResult)
            setAssessmentId(detail.id)
            setPhase('claim-reward')
          } else {
            setPhase('text-phase')
          }
          return
        }

        // 4. Check for COMPLETED + uncredited → claim reward
        const completed = allAssessments.find((a) => a.status === 'COMPLETED')
        if (completed) {
          const detail = await getAssessment(completed.id)
          if (detail.finalResult) {
            setFinalResult(detail.finalResult)
            setAssessmentId(detail.id)
            setPhase('claim-reward')
            return
          }
        }

        // 5. No usable assessment → setup
        setPhase('setup')
      } catch {
        toast.error('Failed to load assessment')
        onBack()
      }
    }

    init()
  }, [projectId, language, onBack])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  const startAssessment = useCallback(
    async (selfLevel: CefrLevel) => {
      setStarting(true)
      try {
        const data = await createAssessment({ selfAssessedLevel: selfLevel, language })
        setAssessmentId(data.id)
        setExpiresAt(data.expiresAt)
        setPhase('text-phase')
      } catch {
        toast.error('Failed to start assessment')
      } finally {
        setStarting(false)
      }
    },
    [language]
  )

  const claimReward = useCallback(async () => {
    if (!assessmentId || !taskIdRef.current) return
    setClaiming(true)
    try {
      await submitTask(taskIdRef.current, assessmentId)
      setPhase('results')
      onCompleted()
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : ''

      if (msg.includes('already been credited')) {
        toast.error('This assessment has already been credited to another task.')
        setPhase('setup')
      } else {
        toast.error('Failed to claim reward')
      }
    } finally {
      setClaiming(false)
    }
  }, [assessmentId, submitTask, onCompleted])

  const handleTextCompleted = useCallback(() => {
    setPhase('speech-phase')
  }, [])

  const handleAllRecorded = useCallback(async () => {
    if (!assessmentId) return
    setPhase('completing')
    try {
      await completeAssessment(assessmentId)
      pollForResult(assessmentId)
    } catch {
      toast.error('Failed to submit for evaluation')
      setPhase('speech-phase')
    }
  }, [assessmentId, pollForResult])

  return {
    phase,
    assessmentId,
    expiresAt,
    finalResult,
    starting,
    claiming,
    startAssessment,
    claimReward,
    handleTextCompleted,
    handleAllRecorded
  }
}
